/*
 * Public API Surface of utils
 */
// Secured Resources Module
export * from './lib/core-utils';
export * from './lib/data-retrievers';
export { MiqSecuredResourcesModule } from './lib/miq-secure-resources/miq-secured-resources.module';
export { MiqSecuredResourcesService } from './lib/miq-secure-resources/miq-secured-resources.service';

export { MiqSecuredResourcesConfig } from './lib/miq-secure-resources/miq-secured-resources.token';
export { MiqImagesModule } from './lib/ng-images/miq-images.module';

// Export feature toggle token and interface
export { FEATURE_TOGGLE_TOKEN, FeatureToggleServiceInterface } from './lib/feature-toggles.token';

export { UserSettings } from './lib/user-settings/user-settings.class';
export {
  AddAdditionalImageDialogComponent,
  AdditionalImageDialogInputData,
} from './lib/ng-images/add-additional-image-dialog/add-additional-image-dialog';
export { SecureImage } from './lib/ng-images/bg-image.models';
export {
  MiqNumber,
  MiqBool,
  boolToString,
  formatNumber,
  // L2-S6: `formatNumber` IGNORES its locale argument by design; `formatNumberForLocale` honours it,
  // matching the host's @shared/utils/helpers/sanitizers copy. See lib/misc/sanitizers.ts.
  formatNumberForLocale,
  formatDate,
  isMasked,
  MiqMoment,
} from './lib/misc/sanitizers';
export {
  normalizeNumericString,
  getCurrentLocale,
  getLocaleIdFromStorage,
  normalizePolishNumber,
  formatNumberValue,
  formatNumberValueForLocale,
  mapLocaleIdToAngularLocale,
  SupportedLocale,
} from './lib/misc/locale-utils';

/*
 * L3: the number-format locale partition. THE SINGLE SOURCE OF TRUTH lives here (this library sits
 * at the bottom of the one-way lib chain); `@miq/shared-models`' `ce-locales.const.ts` is now a
 * re-export shim onto these, so `CE_LOCALES` etc. imported from either package are the same objects.
 */
export {
  CE_LOCALES,
  CE_COMMA_SPACE_DECIMAL_LOCALES,
  CE_COMMA_DOT_DECIMAL_LOCALES,
  CE_SPACE_COMMA_LOCALES,
  CE_DOT_COMMA_LOCALES,
  PLAIN_SPACE,
  NBSP,
  NARROW_NBSP,
  SPACE_SEPARATOR_VARIANTS,
  isCELocaleCode,
  isSpaceVariant,
  isGroupingChar,
  getLocaleNumberStyle,
  getMiqNumberSeparators,
} from './lib/misc/ce-locales.const';
export type { MiqNumberStyle, MiqNumberSeparators } from './lib/misc/ce-locales.const';

/*
 * L3: THE CANONICAL number formatter + its exact inverse. New code must use these rather than
 * `formatNumber` / `formatNumberValue` / `normalizePolishNumber` / a bare `replace(/,/g, '')`.
 */
export {
  formatMiqNumber,
  parseMiqNumber,
  isCELocale,
  getCurrentLocaleNumberStyle,
  getCurrentLocaleSeparators,
} from './lib/misc/locale-number';
export type { MiqNumberFormatOptions, MiqNumberParseOptions } from './lib/misc/locale-number';
export * from './lib/misc/strings.constants';
// L2-S13: moved from @shared/functions so TimetrackingService could reach @miq/platform.
export * from './lib/misc/lower-no-spaces';
export * from './lib/misc/property-type-map';
export { abbrevNumber } from './lib/numbers/numeric-abbreviation.helper';
export * from './lib/uom/uom-abbreviation';
export {
  formatUomValues,
  UomFormat,
  formatValueWithUom,
  formatUomCurrency,
  formatValueWithPrefixedUom,
  formatAmountObject,
} from './lib/uom/uom-values';
export * from './lib/uom/currency/symbol';
export * from './lib/uom-calculator/amount.model';
export * from './lib/uom-calculator/amount.helpers';
export * from './lib/uom-calculator/units-definition';
export * from './lib/uom-calculator/amount-operations';
export * from './lib/image';
export * from './lib/ng-drag-and-drop-list';
export * from './lib/ng-images/bg-image.component';
export * from './lib/formatting/format-number.pipe';
export * from './lib/ng-drag-and-drop-list/drag-and-drop-lists/drag-and-drop-lists.component';
// translation module
export * from './lib/miq-translation/translation.pipe';
export * from './lib/miq-translation/models/translator';
export * from './lib/miq-translation/models/translator-service';
export * from './lib/storage-util';
export * from './lib/secure-sanitizers';

/*
 * L2-R5: dependency-light leaves promoted out of src/app so the host and the reports remote
 * import ONE copy instead of the remote bundling its own. Each old host path is now a
 * re-export shim of the module listed here.
 */
export * from './lib/misc/clean-object';
export * from './lib/misc/rent-validators';
export * from './lib/records/update-record-list-item';
export * from './lib/tree/expandable-resource';
/* L2-S1/T7: the two mat-tree helpers CreateSelectFoldersComponent needs, moved alongside it. */
export * from './lib/tree/get-parent-node';
export * from './lib/tree/tree-nodes-replacer';
export * from './lib/tree-node';
export * from './lib/folders';
export * from './lib/composers/company-contacts';
export * from './lib/composers/base-composer';
export * from './lib/composers/tim-elements/target-location';
export * from './lib/composers/tim';

/*
 * L2-R7: dependency-light leaves promoted out of src/app so the host and the reports remote import
 * ONE copy. Each old host path is now a re-export shim of the module listed here.
 */
export * from './lib/misc/first-element';
export * from './lib/reports/report-name-extractors';
